import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  useToast,
  VStack,
  Spinner,
  Divider,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { contentItems } from '../../../hooks/content/contentItems'

import LazyLoadImg from '../../../components/image/LazyLoadImg'
import VideoPlayer from '../../../components/cards/VideoPlayer'

import { ArrowLeftIcon, BarChart012Icon, Pencil02Icon, ImageXIcon, Trash04Icon, PencilLineIcon } from '../../../assets/icons'
import EditableField from '../../../components/workforce/people/peopleDetails/EditableField'
import { deleteContent, fetchContent, fetchContentList, patchContent } from '../../../api/content/content'
import DiscardModal from '../../../components/modals/discardModal'
import handleToast from '../../../components/HandleToast'
import AboutContentQuestions from './AboutContentQuestions'
import { ContentType, ContentDataResponse } from '../../../types/content/content'
import typeMapping from '../../../themes/contentTypeMapping'
import { useRole } from '../../../hooks/auth/useRole'
import { CONTENT_EXPERIENCE, contentModeCopy } from './contentExperienceStyles'

interface AboutContentProps {
  contentId?: string
  isModal?: boolean
  onClose?: () => void
}

const AboutContent: React.FC<AboutContentProps> = ({ contentId, isModal = false, onClose }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const effectiveId = isModal ? contentId : id
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const { useGetContentItemsListQuery } = contentItems()
  const [contentListData, setContentListData] = useState<ContentDataResponse[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [contentData, setContentData] = useState<ContentDataResponse | undefined>()

  const contentItemsQuery = effectiveId
    ? useGetContentItemsListQuery({
        id: effectiveId,
        // Do not load items initially for training/survey/learning
        enabled: !!contentData && !['training', 'survey', 'learning'].includes(contentData?.content_type),
      } as any)
    : { data: null, isLoading: false }

  const content = useQuery({
    queryKey: ['content', effectiveId],
    queryFn: () => (effectiveId ? fetchContent(effectiveId) : Promise.reject(t('content.errors.noContentId'))),
    enabled: !!effectiveId,
  })
  useEffect(() => {
    if (content?.data) {
      setContentData(content.data)
    }
  }, [content?.data])

  const contentList = useQuery({
    queryKey: ['contentList'],
    queryFn: () => fetchContentList({ page: 1, pageSize: 10, search: '', filters: {} }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  useEffect(() => {
    if (contentList.data && Array.isArray(contentList.data.data) && contentListData.length === 0) {
      const selectedContent = contentList.data.data
        .filter((item: any) => item.id !== id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
      setContentListData(selectedContent)
    }
  }, [contentList.data, contentListData.length, id])

  const isTrainingOrSurveyOrLearning =
    contentData?.content_type === 'training' ||
    contentData?.content_type === 'survey' ||
    contentData?.content_type === 'learning'

  const [isScrolled, setIsScrolled] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isBoxVisible, setIsBoxVisible] = useState(false)

  const updateContentMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: Partial<ContentDataResponse> }) => patchContent(id, content),
    onSuccess: () => {
      handleToast(toast, true, t('content.toasts.updatedTitle'), t('content.toasts.updatedMessage'))
      queryClient.invalidateQueries({ queryKey: ['content', id] })
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('content.toasts.updateFailedTitle'), error.message)
    },
  })
  const [isEditing, setIsEditing] = useState(false)
  const currentContentId = effectiveId || id

  const handleSave = (field: string, newValue: string) => {
    if (!currentContentId) return
    updateContentMutation.mutate({
      id: currentContentId,
      content: { [field]: newValue },
    })
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && currentContentId) {
      const validTypes = ['image/png', 'image/gif', 'image/jpeg', 'video/mp4']
      if (!validTypes.includes(file.type)) {
        handleToast(toast, false, t('content.toasts.invalidFileTypeTitle'), t('content.toasts.invalidFileTypeMessage'))
        return
      }

      try {
        const result = await updateContentMutation.mutateAsync({
          id: currentContentId,
          content: { thumbnail: file },
        })

        setContentData((prev) => prev ? ({ ...prev, thumbnail: result.thumbnail }) : prev)
      } catch (error) {
        console.error('Error uploading thumbnail:', error)
        handleToast(toast, false, t('content.toasts.uploadFailedTitle'), t('content.toasts.uploadFailedMessage'))
      }
    }
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 0)
  }

  const handleDelete = async () => {
    try {
      if (!currentContentId) {
        throw new Error(t('content.errors.noContentId'))
      }
      await deleteContent(currentContentId)
      queryClient.invalidateQueries({ queryKey: ['content'] })
    } catch (error) {
      handleToast(toast, false, t('common.states.error'), t('messages.deleteFailed'))
    }
    if (isModal && onClose) {
      onClose()
    } else {
      navigate('/user/content')
    }
    setShowDiscardModal(false)
  }

  const handleEditItemsNavigate = () => {
  if (!currentContentId) return

  if (
    contentData?.content_type === 'training' ||
    contentData?.content_type === 'survey' ||
    contentData?.content_type === 'learning'
  ) {
    queryClient.invalidateQueries({ queryKey: ['contentItemsList', currentContentId] })
    navigate(`/user/content/${currentContentId}/questionnaire`, {
      state: {
        contentId: currentContentId,
        contentType: contentData?.content_type,
        is_draft: contentData?.is_draft,
        minimum_score: contentData?.minimum_score,
      },
    })
    return
  }

  navigate(`/user/content/${currentContentId}/edit-items`, {
    state: {
      contentId: Number(currentContentId),
      selectedType: contentData?.content_type,
      title: contentData?.title,
      description: contentData?.description,
      topic: contentData?.topic,
      thumbnail: contentData?.thumbnail || null,
      fromCreateButton: false,
      progressStep: 2,
      itemsPersisted: true,
      sourceTitle: t('content.pages.detailTitle'),
    },
  })
}

const handleEditContent = () => {
  if (!currentContentId) return

  if (
    contentData?.content_type === 'training' ||
    contentData?.content_type === 'survey' ||
    contentData?.content_type === 'learning'
  ) {
    queryClient.invalidateQueries({ queryKey: ['contentItemsList', currentContentId] })
    navigate(`/user/content/${currentContentId}/questionnaire`, {
      state: {
        contentId: currentContentId,
        contentType: contentData?.content_type,
        is_draft: contentData?.is_draft,
        minimum_score: contentData?.minimum_score,
      },
    })
    return
  }

  navigate(`/user/content/${currentContentId}/edit-items`, {
    state: {
      contentId: currentContentId,
      selectedType: contentData?.content_type,
      title: contentData?.title,
      description: contentData?.description,
      topic: contentData?.topic,
      thumbnail: contentData?.thumbnail || null,
      fromCreateButton: false,
      progressStep: 2,
      itemsPersisted: true,
      isEditMode: true,
    },
  })
}
  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleOpenQuestionnaire = () => {
    if (!currentContentId) return
    queryClient.invalidateQueries({ queryKey: ['contentItemsList', currentContentId] })
    navigate(`/user/content/${currentContentId}/questionnaire`, {
      state: {
        contentId: currentContentId,
        contentType: contentData?.content_type,
        is_draft: contentData?.is_draft,
        minimum_score: contentData?.minimum_score,
      },
    })
  }

  const mapping =
    contentData?.content_type && typeMapping[contentData.content_type as keyof typeof typeMapping]
      ? typeMapping[contentData.content_type as keyof typeof typeMapping]
      : {
	          label: contentData?.content_type
	            ? contentData.content_type.charAt(0).toUpperCase() + contentData.content_type.slice(1)
	            : '',
	          bg: 'solid.0',
	          borderColor: 'solid.100',
	          color: 'solid.600',
	          icon: <BarChart012Icon />,
	        }

  const typeLabel = (() => {
    const contentType = contentData?.content_type
    if (!contentType) return ''
    if (contentType === 'training') return t('content.types.trainAndTest')

    const key = `content.types.${contentType}`
    const translated = t(key)
    if (translated !== key) return translated

    return contentType.charAt(0).toUpperCase() + contentType.slice(1)
  })()

  useEffect(() => {
    let visibleElements = 0

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleElements++
          } else {
            visibleElements--
          }
        })

        setIsBoxVisible(visibleElements > 0)
      },
      { threshold: [0] },
    )

    const boxElements = Array.from(document.getElementsByClassName('control-box'))
    boxElements.forEach((element) => {
      if (element) {
        observer.observe(element)
      }
    })

    return () => {
      boxElements.forEach((element) => {
        if (element) {
          observer.unobserve(element)
        }
      })
    }
  }, [])

  const { isAdmin, isUserMode } = useRole()
  const isAdminEditingView = isAdmin && !isUserMode
  const isTakingView = !isAdmin || isUserMode
  const flowActionLabel =
    contentData?.content_type === 'training'
      ? isAdminEditingView
        ? t('content.actions.editTraining')
        : t('content.actions.startTraining')
      : contentData?.content_type === 'survey'
        ? isAdminEditingView
          ? t('content.actions.editSurvey')
          : t('content.actions.startSurvey')
        : contentData?.content_type === 'learning'
          ? isAdminEditingView
            ? t('content.actions.editLearning')
            : t('content.actions.startLearning')
          : ''
  const flowHeading = isAdminEditingView ? contentModeCopy.admin.heading : contentModeCopy.user.heading
  const flowDescription = isAdminEditingView ? contentModeCopy.admin.description : contentModeCopy.user.description

  return (
    <Flex
      flexDirection="column"
      height="100%"
      overflowY="auto"
      mr={isModal ? 0 : '0'}
      onScroll={handleScroll}
      bg={CONTENT_EXPERIENCE.pageBg}
      borderRadius="8px"
      
    >
      <Card width="100%" height={'auto'} px="32px" py="24px" borderRadius={CONTENT_EXPERIENCE.panelRadius} boxShadow="none" bg="transparent">
        {/* Header Section */}
        <Flex 
          alignItems="center" 
          gap="16px" 
          mb="32px"
          pb="24px"
          borderBottom="1px solid"
          borderColor="solid.50"
        >
          {!isModal && (
            <Button
              onClick={() => navigate('/user/content')}
              aria-label={t('common.actions.back')}
              variant="icon"
            >
              <Icon fill="none" color="solid.700">
                <ArrowLeftIcon />
              </Icon>
            </Button>
          )}
          <Skeleton isLoaded={!content.isLoading} height="28px" className="control-box">
            <Text fontSize="20px" fontWeight="600" color="solid.900">
              {t('content.pages.detailTitle')}
            </Text>
          </Skeleton>
        </Flex>
        {/* Main Content Area */}
        <Flex justify="center">
          <Flex direction={{ base: 'column', md: 'row' }} gap="32px" width={'100%'} maxW="1400px">
            <VStack flex="1" spacing="24px" align="stretch">
              {/* Thumbnail Section */}
              <Box
                bg="solid.0"
                border="1px solid"
                height={contentData?.thumbnail ? 'auto' : '320px'}
                width="100%"
                maxH="450px"
                borderColor="solid.50"
                borderRadius="12px"
                overflow="hidden"
                className="control-box"
                boxShadow="0 2px 8px rgba(0,0,0,0.04)"
              >
                <Skeleton isLoaded={!content.isLoading} height="100%" width="100%" borderRadius="8px">
                  <Box
                    position="relative"
                    width="100%"
                    height="100%"
                    borderRadius="8px"
                    overflow="hidden"
                    onClick={isEditing ? () => fileInputRef.current?.click() : handleOpenModal}
                    cursor={isEditing ? 'pointer' : 'default'}
                    sx={{
                      '&:hover .overlay': { opacity: 1 },
                    }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*, video/mp4"
                      style={{ display: 'none' }}
                    />
                    {!content.isLoading ? (
                      contentData?.thumbnail ? (
                        contentData.thumbnail.includes('.mp4') ? (
                          <Box
                            position="relative"
                            width="100%"
                            height="100%"
                            display="flex"
                            bg="gray.800"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <video width="100%" height="100%" preload="none">
                              <source src={contentData.thumbnail} type="video/mp4" />
                            </video>
                            <Icon
                              as="svg"
                              viewBox="0 0 24 24"
                              boxSize="60px"
                              color="white"
                              position="absolute"
                              filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.5))"
                            >
                              <path fill="currentColor" d="M8 5v14l11-7z" />
                            </Icon>
                          </Box>
                        ) : (
                          <LazyLoadImg
                            src={contentData.thumbnail}
                            alt={t('content.images.thumbnailAlt')}
                            borderRadius="8px"
                            loading="lazy"
                            className="thumbnail-image"
                            objectFit="cover"
                            style={{
                              transitionDelay: '0.3s',
                              width: '100%',
                              height: '100%',
                            }}
                          />
                        )
                      ) : (
                        <Flex
                          width="100%"
                          height="100%"
                          alignItems="center"
                          justifyContent="center"
                          bg="solid.25"
                          flexDirection="column"
                        >
                          <Icon boxSize="50px" color="solid.100">
                            <ImageXIcon />
                          </Icon>
                        </Flex>
                      )
                    ) : (
                      <Flex width="100%" height="100%" alignItems="center" justifyContent="center" bg="solid.25">
                        <Skeleton height="100%" width="100%" />
                      </Flex>
                    )}
                    {isEditing && (
                      <Flex
                        className="overlay"
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        align="center"
                        justify="center"
                        opacity={0}
                        transition="opacity 0.3s ease-in-out"
                        bg="rgba(0, 0, 0, 0.5)"
                      >
                        <Icon color="solid.0" boxSize="30px">
                          <Pencil02Icon />
                        </Icon>
                      </Flex>
                    )}
                  </Box>
                  {contentData?.thumbnail && isModalOpen && (
                    <Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered size="4xl" closeOnOverlayClick>
                      <ModalOverlay />
                      <ModalContent>
                        <ModalBody p={0}>
                          <Skeleton isLoaded={!content.isLoading}>
                            {contentData?.thumbnail.includes('.mp4') ? (
                              <VideoPlayer url={contentData.thumbnail} onRemove={() => {}} startPlay={false} />
                            ) : (
                              <LazyLoadImg
                                src={contentData.thumbnail}
                                alt={t('content.images.previewAlt')}
                                loading="lazy"
                                objectFit="contain"
                                style={{ width: '100%', height: 'auto' }}
                              />
                            )}
                          </Skeleton>
                        </ModalBody>
                      </ModalContent>
                    </Modal>
                  )}
                </Skeleton>
              </Box>

              {/* Content Info Card */}
              <Box
                bg="background.White"
                border="1px solid"
                borderColor="solid.50"
                borderRadius={CONTENT_EXPERIENCE.cardRadius}
                p="24px"
                boxShadow="none"
              >
                <Flex width="100%" alignItems="flex-start" justifyContent="space-between" mb="20px">
                  <VStack align="start" spacing="12px" flex="1">
                    <HStack spacing="12px" flexWrap="wrap">
                      {isEditing ? (
                        <EditableField
                          value={contentData?.title || t('content.labels.noTitleAvailable')}
                          onSave={(newValue) => handleSave('title', newValue)}
                          hideLabel
                          alwaysEditable
                          isEditing
                          textStyleOnDisplay="h-lg"
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <Skeleton isLoaded={!content.isLoading} height="32px">
                          <Text fontSize="24px" fontWeight="700" color="solid.900">
                            {contentData?.title}
                          </Text>
                        </Skeleton>
                      )}

                      <Skeleton isLoaded={!content.isLoading} height="28px">
                        <Flex
                          alignItems="center"
                          fontSize="13px"
                          fontWeight="600"
                          color={mapping.color}
                          borderRadius="8px"
                          borderWidth="1px"
                          backgroundColor={mapping.bg}
                          borderColor={mapping.borderColor}
                          px="12px"
                          py="6px"
                          gap="6px"
                          h="28px"
                        >
                          <Icon width="14px" height="14px" fill="none" color={mapping.color}>
                            {mapping.icon}
                          </Icon>
                          <Text fontSize="12px" fontWeight="600">{typeLabel}</Text>
                        </Flex>
                      </Skeleton>
                    </HStack>

                    <Skeleton isLoaded={!content.isLoading} height="20px" mt="8px">
                      <Text fontSize="14px" fontWeight="500" color="solid.600">
                        {contentData?.topic || t('content.labels.noTopic')}
                      </Text>
                    </Skeleton>
                  </VStack>

                  {!isModal && isAdminEditingView && (
                    <Skeleton isLoaded={!content.isLoading}>
                      <HStack spacing="12px">
                        <Button
                          onClick={() => setShowDiscardModal(true)}
                          variant="error"
                        >
                          <Icon fill="none" color="error.100">
                            <Trash04Icon />
                          </Icon>
                          {t('common.actions.delete')}
                        </Button>
                        <Button
  onClick={handleEditContent}
  aria-label={t('common.actions.edit')}
  spinner={<Spinner color="solid.500" />}
  isLoading={content.isLoading}
  variant="mono"
>
  <Icon fill="none" color="white">
    <PencilLineIcon />
  </Icon>
  {t('common.actions.edit')}
</Button>
                      </HStack>
                    </Skeleton>
                  )}
                </Flex>

                <Box width="100%" height="1px" bg="solid.50" my="20px"></Box>

                <Box width="100%">
                  <Skeleton isLoaded={!content.isLoading}>
                    {isEditing ? (
                      <EditableField
                        label={t('common.fields.description')}
                        value={contentData?.description || ''}
                        onSave={(newValue) => handleSave('description', newValue)}
                        hideLabel
                        width="100%"
                        height="100%"
                        alwaysEditable
                        isEditing
                        textStyleOnDisplay="l-s"
                        isTextArea
                      />
                    ) : (
                      <Text fontSize="14px" lineHeight="1.6" color="solid.700" whiteSpace="pre-wrap">
                        {contentData?.description || t('content.labels.noDescription')}
                      </Text>
                    )}
                  </Skeleton>
                </Box>
              </Box>

              {!contentList.isLoading && !isModal && (
                <Box w="100%">
                  {isTrainingOrSurveyOrLearning ? (
                    <Box>
                      <Skeleton isLoaded={!content.isLoading}>
                        <Button onClick={handleOpenQuestionnaire} variant="unstyled" width="100%">
                          <Flex
                            borderWidth="1px"
                            borderRadius={CONTENT_EXPERIENCE.cardRadius}
                            minH="132px"
                            borderColor="solid.100"
                            bg="background.White"
                            overflow="hidden"
                            position="relative"
                            align="center"
                            boxShadow="none"
                            width="100%"
                          >
                            {contentData?.thumbnail && (
                              <Box w="140px" h="100%" flexShrink={0}>
                                <LazyLoadImg
                                  src={contentData?.thumbnail || ''}
                                  borderTopLeftRadius="12px"
                                  borderBottomLeftRadius="12px"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            )}
                            <Flex flex="1" px="20px" py="20px" direction="column" align="flex-start" justify="center">
                              <Text fontSize="18px" fontWeight="700" color="solid.900" mb="6px" noOfLines={2} textAlign="left">
                                {flowHeading}
                              </Text>
                              <Text fontSize="14px" color="solid.600" mb="10px" noOfLines={2} textAlign="left">
                                {flowDescription}
                              </Text>
                              <Text fontSize="12px" fontWeight="600" color="solid.500" mb="8px" textTransform="uppercase" letterSpacing="0.5px">
                                {flowActionLabel}
                              </Text>
                              <Text fontSize="15px" fontWeight="600" color="solid.900" noOfLines={2} textAlign="left">
                                {contentData?.title || t('content.labels.noTitleAvailable')}
                              </Text>
                            </Flex>
                          </Flex>
                        </Button>
                      </Skeleton>
                    </Box>
                  ) : (
                    <Box w="100%">
                      {id && contentData && (
                        <AboutContentQuestions
                          contentId={id}
                          contentType={contentData.content_type || ContentType.Skills}
                          isDraft={contentData.is_draft}
                          stopSidebar={isBoxVisible}
                          onEditNavigate={handleEditItemsNavigate}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </VStack>
          </Flex>
        </Flex>
        <DiscardModal
          show={showDiscardModal}
          notRow={true}
          handleClose={() => setShowDiscardModal(false)}
          handleDelete={handleDelete}
          action="delete"
          selectedRowsCount={1}
        />
      </Card>
    </Flex>
  )
}

export default AboutContent
